/**
 * Unified Serial Number Input Component
 * Centralized component for serial number input across the application
 */

import React, {useState, useCallback, useMemo} from 'react';
import Button from '../../../Presentation/components/ui/Button';

export interface UnifiedSerialNumberInputProps {
  // Core props
  value: string[];
  onChange: (serials: string[]) => void;

  // Configuration
  context?: 'product' | 'stock' | 'distribution';

  // Validation
  required?: boolean;
  maxSerials?: number;

  // UI Configuration
  placeholder?: string;
  label?: string;
  title?: string;
  warningMessage?: string;
  showBulkInput?: boolean;
  showCopyButton?: boolean;
  showClearButton?: boolean;
  showSerialCount?: boolean;

  // Styling
  className?: string;
  disabled?: boolean;

  // Error handling
  error?: string;
}

export const UnifiedSerialNumberInput: React.FC<UnifiedSerialNumberInputProps> = ({
  value = [],
  onChange,
  context = 'product',
  required = false,
  maxSerials = 1000,
  label = 'الأرقام التسلسلية',
  title,
  warningMessage,
  showBulkInput = true,
  showCopyButton = true,
  showClearButton = true,
  showSerialCount = true,
  className = '',
  disabled = false,
  error
}) => {
  const [showBulkInputState, setShowBulkInputState] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});


  // Ensure value is always an array
  const safeValue = useMemo(() => Array.isArray(value) ? value : [], [value]);

  // Context-specific configurations
  const getContextConfig = () => {
    switch (context) {
      case 'product':
        return {
          title: title || 'السيريالات',
          warningMessage: warningMessage || '',
          placeholder: 'أدخل الرقم التسلسلي',
          required: false
        };

      case 'stock':
        return {
          title: title || 'السيريالات',
          warningMessage: warningMessage || '',
          placeholder: 'أدخل الرقم التسلسلي',
          required: false
        };

      case 'distribution':
        return {
          title: title || 'السيريالات',
          warningMessage: warningMessage || '',
          placeholder: 'أدخل الرقم التسلسلي',
          required: true
        };

      default:
        return {
          title: title || 'السيريالات',
          warningMessage: warningMessage || '',
          placeholder: 'أدخل الرقم التسلسلي',
          required: required
        };
    }
  };

  const contextConfig = getContextConfig();


  // Add a new serial
  const addSerial = useCallback(() => {
    if (safeValue.length < maxSerials) {
      onChange([...safeValue, '']);
    }
  }, [safeValue, onChange, maxSerials]);

  // Remove a serial
  const removeSerial = useCallback((index: number) => {
    const newSerials = safeValue.filter((_, i) => i !== index);
    onChange(newSerials);

    // Clear error for this serial
    const newErrors = {...validationErrors};
    delete newErrors[`serial_${index}`];
    setValidationErrors(newErrors);
  }, [safeValue, onChange, validationErrors]);

  // Update a serial
  const updateSerial = useCallback((index: number, newValue: string) => {
    const newSerials = [...safeValue];
    newSerials[index] = newValue;
    onChange(newSerials);

    // Clear error for this serial
    const newErrors = {...validationErrors};
    delete newErrors[`serial_${index}`];
    setValidationErrors(newErrors);
  }, [safeValue, onChange, validationErrors]);

  // Clear all serials
  const clearSerials = useCallback(() => {
    onChange([]);
    setValidationErrors({});
  }, [onChange]);

  // Add bulk serials
  const handleBulkInput = useCallback(() => {
    if (!bulkInput.trim()) {return;}

    const newSerials = bulkInput
      .split(/[,\n\s]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);

    if (newSerials.length > 0) {
      const combinedSerials = [...safeValue, ...newSerials].slice(0, maxSerials);
      onChange(combinedSerials);
      setBulkInput('');
      setShowBulkInputState(false);
    }
  }, [bulkInput, safeValue, onChange, maxSerials]);

  // Copy serials to clipboard
  const copySerials = useCallback(async () => {
    const validSerials = safeValue.filter(s => s.trim());
    if (validSerials.length > 0) {
      try {
        await navigator.clipboard.writeText(validSerials.join('\n'));
      } catch (error) {
        // Silently handle clipboard errors - clipboard API may not be available
        // or user may have denied clipboard permissions
        // This is a non-critical UX feature, failure is acceptable
      }
    }
  }, [safeValue]);

  // Validate on change - call validation directly when value changes
  React.useEffect(() => {
    const errors: Record<string, string> = {};

    // Check for duplicates
    const uniqueSerials = [...new Set(safeValue.filter(s => s.trim()))];
    if (uniqueSerials.length !== safeValue.filter(s => s.trim()).length) {
      errors.duplicates = 'يوجد تكرار في الأرقام التسلسلية';
    }

    // Validate each serial (inline validation to avoid dependency issues)
    safeValue.forEach((serial, index) => {
      if (!serial || serial.trim().length === 0) {
        const error = required ? 'رقم التسلسل مطلوب' : null;
        if (error) {
          errors[`serial_${index}`] = error;
        }
        return;
      }

      const trimmedSerial = serial.trim();

      if (trimmedSerial.length < 3) {
        errors[`serial_${index}`] = 'رقم التسلسل يجب أن يكون 3 أحرف على الأقل';
      } else if (trimmedSerial.length > 50) {
        errors[`serial_${index}`] = 'رقم التسلسل يجب أن يكون أقل من 50 حرف';
      } else if (!/^[A-Za-z0-9\-_]+$/.test(trimmedSerial)) {
        errors[`serial_${index}`] = 'رقم التسلسل يجب أن يحتوي على أحرف وأرقام فقط';
      }
    });

    // Check required validation
    if (required && safeValue.filter(s => s.trim()).length === 0) {
      errors.required = 'يجب إدخال رقم تسلسلي واحد على الأقل';
    }

    setValidationErrors(errors);
  }, [safeValue, required]);

  const validSerials = safeValue.filter(s => {
    if (!s || s.trim().length === 0) {return false;}
    const trimmed = s.trim();
    return trimmed.length >= 3 &&
           trimmed.length <= 50 &&
           /^[A-Za-z0-9\-_]+$/.test(trimmed);
  });
  const serialCount = validSerials.length;

  return (
    <div className={`space-y-4 ${className}`}>

      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
          {label} {required && '*'}
        </label>
      )}

      {/* Main Input Area */}
      <div className="p-4 border border-gray-200 rounded-lg bg-brand-50">

        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          {showBulkInput && (
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => setShowBulkInputState(!showBulkInputState)}
              className="flex-1 border border-gray-300 hover:border-brand-500 text-brand-600"
              disabled={disabled}
            >
              {showBulkInputState ? 'إلغاء الإضافة المتعددة' : 'إضافة متعددة'}
            </Button>
          )}

          {validSerials.length > 0 && (
            <>
              {showCopyButton && (
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={copySerials}
                  className="px-3 py-1 border border-gray-300 hover:border-green-500 text-green-600"
                  disabled={disabled}
                >
                  نسخ
                </Button>
              )}
              {showClearButton && (
                <Button
                  type="button"
                  variant="danger"
                  size="small"
                  onClick={clearSerials}
                  className="px-3 py-1"
                  disabled={disabled}
                >
                  مسح الكل
                </Button>
              )}
            </>
          )}
        </div>

        {/* Bulk Input */}
        {showBulkInputState && (
          <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="text-sm text-yellow-800 mb-2 flex items-center gap-2">
              <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              يمكنك إضافة عدة سيريلات دفعة واحدة. استخدم الفاصلة، السطر الجديد، أو المسافة للفصل بين السيريلات.
            </div>
            <textarea
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
              placeholder="أدخل السيريلات مفصولة بفاصلة أو سطر جديد
مثال: SN001, SN002, SN003
أو
SN001
SN002
SN003"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-brand-500 resize-none"
              rows={4}
              dir="ltr"
              disabled={disabled}
            />
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant="primary"
                size="small"
                onClick={handleBulkInput}
                disabled={!bulkInput.trim() || disabled}
                className="flex-1"
              >
                إضافة السيريلات
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="small"
                onClick={() => {
                  setBulkInput('');
                  setShowBulkInputState(false);
                }}
                className="px-3"
              >
                إلغاء
              </Button>
            </div>
          </div>
        )}

        {/* Serial Count */}
        {showSerialCount && serialCount > 0 && (
          <div className="flex justify-between items-center mb-2 text-sm">
            <span className="text-gray-600">عدد السيريلات المضافة:</span>
            <span className="font-semibold text-brand-600 bg-brand-100 px-2 py-1 rounded-full">
              {serialCount}
            </span>
          </div>
        )}

        {/* Individual Serial Inputs */}
        <div className="space-y-2">
          {safeValue.length === 0 ? (
            <div className="text-center py-4 text-gray-500 text-sm">
              <div className="mb-2">
                <svg className="w-8 h-8 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
              </div>
              لا توجد سيريلات مضافة بعد
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={addSerial}
                  disabled={disabled}
                  className="text-brand-600 border-brand-300 hover:bg-brand-50"
                >
                  + إضافة أول رقم تسلسلي
                </Button>
              </div>
            </div>
          ) : (
            <>
              {safeValue.map((serial, index) => (
                <div key={index} className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={serial}
                      onChange={(e) => updateSerial(index, e.target.value)}
                      placeholder={contextConfig.placeholder}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-brand-500 disabled:bg-gray-100 disabled:cursor-not-allowed relative z-50 ${
                        validationErrors[`serial_${index}`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{zIndex: 50}}
                      dir="ltr"
                      disabled={disabled}
                    />
                    <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 bg-white px-1">
                      #{index + 1}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    size="small"
                    onClick={() => removeSerial(index)}
                    className="px-3 py-2"
                    disabled={disabled}
                  >
                    حذف
                  </Button>
                </div>
              ))}

              <Button
                type="button"
                variant="ghost"
                onClick={addSerial}
                className="w-full mt-2 border-2 border-dashed border-gray-300 hover:border-brand-500 disabled:border-gray-200 disabled:cursor-not-allowed"
                disabled={disabled || safeValue.length >= maxSerials}
              >
                + إضافة رقم تسلسلي آخر
              </Button>
            </>
          )}
        </div>

        {/* Error Display */}
        {(error || Object.keys(validationErrors).length > 0) && (
          <div className="text-red-600 text-sm mt-2">
            {error || Object.values(validationErrors)[0]}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedSerialNumberInput;
